import { useState } from "react";

import { toast } from "react-toastify";
import { Phone, PhoneCall, PhoneOutgoing, Grid3x3, Loader2 } from "lucide-react";

import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import toastError from "../../errors/toastError";
import usePermissions from "../../hooks/usePermissions";
import { useLigacoes, ligar as ligarWhats } from "../../ligacoes/store";
import { useSoftphone, ligarPara, abrir as abrirDiscador } from "../../softphone/store";
import { paraDiscagem } from "../../softphone/discagem";

// Um único botão de telefone para o ticket. Antes havia botões separados para
// WhatsApp (módulo de ligações) e para VoIP/ramal (SIP), lado a lado — e era
// fácil clicar no errado. Agora um só ícone abre um modal e pergunta por onde
// ligar, mostrando apenas os meios disponíveis naquele momento. Se só um meio
// existe, liga direto, sem modal.
const BotaoLigar = ({ ticket, contact, className }) => {
	const lig = useLigacoes();
	const sp = useSoftphone();
	const { temPermissao } = usePermissions();
	const [aberto, setAberto] = useState(false);
	const [ligando, setLigando] = useState(false);

	const alvo = ticket?.contact || contact;
	const grupo = ticket?.isGroup || alvo?.isGroup;
	const numero = String(alvo?.number || "").replace(/\D/g, "");

	// --- WhatsApp (módulo de ligações / AstraCalls) ---
	const whatsExiste = lig.disponivel && temPermissao("calls:make");
	const emLigacao = Boolean(lig.minha);
	const whatsPode = whatsExiste && !grupo && numero.length >= 10 && !emLigacao;
	const whatsMotivo = grupo
		? "grupo não pode"
		: numero.length < 10
			? "sem número"
			: emLigacao
				? "encerre a ligação atual"
				: null;

	// --- VoIP / ramal (SIP softphone) ---
	const sipExiste = Boolean(sp.conta);
	const discado = paraDiscagem(alvo?.number);
	const sipPode =
		sipExiste && sp.regState === "registered" && !grupo && discado.length >= 10;
	const sipMotivo =
		sp.regState !== "registered"
			? "ramal offline"
			: grupo
				? "grupo não pode"
				: discado.length < 10
					? "sem número"
					: null;

	// Sem nenhum meio configurado, não há botão.
	if (!whatsExiste && !sipExiste) return null;

	const ligarWhatsApp = async () => {
		setAberto(false);
		setLigando(true);
		try {
			await ligarWhats(ticket?.id ? { ticketId: ticket.id } : { number: numero });
		} catch (err) {
			if (err?.response) toastError(err);
			else toast.error(err?.message || "Não foi possível ligar.");
		}
		setLigando(false);
	};

	const ligarSip = () => {
		setAberto(false);
		if (sipPode) ligarPara(discado);
	};

	const abrirDial = () => {
		setAberto(false);
		abrirDiscador();
	};

	// Um meio só disponível para ligar de imediato: liga direto, sem perguntar.
	const clicar = () => {
		const soWhats = whatsExiste && !sipExiste;
		const soSip = sipExiste && !whatsExiste;
		if (soWhats && whatsPode) return ligarWhatsApp();
		if (soSip && sipPode) return ligarSip();
		setAberto(true);
	};

	return (
		<>
			<Button
				variant="ghost"
				size="icon"
				className={className || "h-8 w-8"}
				aria-label="Ligar"
				title="Ligar"
				disabled={ligando}
				onClick={clicar}
			>
				{ligando ? (
					<Loader2 className="h-4 w-4 animate-spin" />
				) : (
					<Phone className="h-4 w-4" />
				)}
			</Button>

			<Dialog open={aberto} onOpenChange={setAberto}>
				<DialogContent className="max-w-xs">
					<DialogHeader>
						<DialogTitle>Como deseja ligar?</DialogTitle>
					</DialogHeader>
					<div className="flex flex-col gap-2">
						{whatsExiste && (
							<Button
								variant="outline"
								className="h-11 justify-start gap-2"
								disabled={!whatsPode}
								title={whatsMotivo ? `WhatsApp — ${whatsMotivo}` : `Ligar pelo WhatsApp para ${numero}`}
								onClick={ligarWhatsApp}
							>
								<PhoneCall className="h-4 w-4 text-emerald-600" />
								<span>WhatsApp{whatsMotivo ? ` — ${whatsMotivo}` : ""}</span>
							</Button>
						)}
						{sipExiste && (
							<>
								<Button
									variant="outline"
									className="h-11 justify-start gap-2"
									disabled={!sipPode}
									title={sipMotivo ? `VoIP — ${sipMotivo}` : `Ligar para ${discado}`}
									onClick={ligarSip}
								>
									<PhoneOutgoing className="h-4 w-4" />
									<span>VoIP / Ramal{sipMotivo ? ` — ${sipMotivo}` : ""}</span>
								</Button>
								<Button
									variant="ghost"
									className="h-9 justify-start gap-2 text-muted-foreground"
									onClick={abrirDial}
								>
									<Grid3x3 className="h-4 w-4" />
									<span>Abrir discador</span>
								</Button>
							</>
						)}
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default BotaoLigar;
